'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  formatMetricValue,
  resolveEncountersMetricCode,
} from 'src/utils/strategyHelpers';
import { getProfileUrl } from 'src/utils/profileUrl';
import { useMetricToggles } from '../../hooks/strategy';

interface Location {
  type?: string;
  provider_id?: string;
  name?: string;
}

interface DisplayOption {
  code: string;
  type?: string;
  label?: string;
}

interface AvailableOption {
  code: string;
  label?: string;
}

interface DisplayOptions {
  metricId?: string;
  availableOptions?: AvailableOption[];
  displayOptions?: DisplayOption[];
}

interface MetricData {
  locations?: Location[];
  [key: string]: number | string | Location[] | null | undefined;
}

interface StrategyDynamicColumnProps {
  field: string;
  row: {
    metrics_v2?: Record<string, MetricData>;
  };
  displayOptions?: DisplayOptions;
}

const StrategyDynamicColumn = ({
  field,
  row,
  displayOptions,
}: StrategyDynamicColumnProps) => {
  const metricData = row.metrics_v2?.[field];
  const { metricToggles } = useMetricToggles();
  const { showEncountersTotals } = useFlags();

  const availableOptions = useMemo(
    () => displayOptions?.availableOptions ?? [],
    [displayOptions?.availableOptions]
  );

  const activeOption =
    metricToggles[field] || availableOptions[0]?.code || 'claims';

  // For encounters (cohort) metrics, read the encounters value under the claims
  // toggle when the flag is on. The toggle keeps its `*_claims` code as its identity
  // (its label is relabeled "Encounters" upstream by resolveEncountersDisplayOptions).
  // The full display_options (including the encounters sibling) detect + format it.
  const effectiveOption = resolveEncountersMetricCode(
    activeOption,
    (displayOptions?.displayOptions ?? []).map((option) => option.code),
    !!showEncountersTotals
  );

  const renderValue = useMemo(() => {
    if (!metricData) return '-';

    // Check the active toggle option's value first so toggling actually works
    if (effectiveOption && effectiveOption in metricData) {
      const value = metricData[effectiveOption];

      // Null/missing value for the selected option — show dash
      if (value == null) return '-';

      // Location arrays (e.g., institution)
      if (Array.isArray(value)) {
        return (
          <div className="text-left min-w-0 w-full" onClick={(e) => e.stopPropagation()}>
            {value.map((location: Location, index: number) => {
              const hasValidData =
                location?.type && location?.provider_id && location?.name;

              return (
                <div
                  key={location.provider_id ?? index}
                  className="text-xs leading-tight truncate"
                >
                  {hasValidData ? (
                    <Link
                      href={getProfileUrl(location.type, location.provider_id)}
                      scroll={false}
                      className="text-sky-900"
                      data-testid={`strategy-dynamic-column-link-${location.provider_id}`}
                    >
                      <span>{location.name}</span>
                      <Icon icon={faExternalLinkAlt} size="sm" className="ml-1" />
                    </Link>
                  ) : (
                    <span className="text-neutral-600">
                      {location?.name ?? 'n/a'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      // Numeric values
      if (typeof value === 'number') {
        const displayOptionConfig = displayOptions?.displayOptions?.find(
          (opt) => opt.code === effectiveOption
        );

        return formatMetricValue(
          value,
          displayOptionConfig?.type,
          effectiveOption
        );
      }

      // String values (e.g., focus, program_type)
      if (typeof value === 'string') {
        return value || '-';
      }
    }

    // Fallback: render locations if available
    if (metricData.locations && Array.isArray(metricData.locations)) {
      return (
        <div className="text-left min-w-0 w-full" onClick={(e) => e.stopPropagation()}>
          {metricData.locations.map((location, index) => {
            const hasValidData =
              location?.type && location?.provider_id && location?.name;

            return (
              <div
                key={location.provider_id ?? index}
                className="text-xs leading-tight truncate"
              >
                {hasValidData ? (
                  <Link
                    href={getProfileUrl(location.type, location.provider_id)}
                    scroll={false}
                    className="text-sky-900"
                    data-testid={`strategy-dynamic-column-link-${location.provider_id}`}
                  >
                    <span>{location.name}</span>
                    <Icon icon={faExternalLinkAlt} size="sm" className="ml-1" />
                  </Link>
                ) : (
                  <span className="text-neutral-600">
                    {location?.name ?? 'n/a'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback: first numeric value
    const firstNumericValue = Object.values(metricData).find(
      (val): val is number => typeof val === 'number'
    );

    if (firstNumericValue !== undefined) {
      return firstNumericValue.toLocaleString();
    }

    return '-';
  }, [metricData, effectiveOption, displayOptions?.displayOptions]);

  return (
    <div className="w-full text-left truncate">
      {renderValue}
    </div>
  );
};

export default StrategyDynamicColumn;
